import { CardService } from "@/services/CardService";
import { ApiResponse, AutocodeResponse, Car, Document, DriverType, InsuranceCard, OTPBankGender, Sex, TokenData } from "@/services/index.defs";
import { plainToClass } from "class-transformer";
import { UserService } from "@/services/UserService";
import { CarDocumentType } from "@/models/Models";
import { v4 as uuidv4 } from "uuid";
import { DefaultMapping } from "_P/copyobj";
import { InsuranceService } from "../services/InsuranceService";
import { RecentCardsHelper } from "@/core/helpers/RecentCardsHelper";
import GeoService from "../services/Geo";

export class InsuranceModule {
    isLoading: boolean = false;
    isSaving: boolean = false;

    step: number = null;

    card: InsuranceCard = new InsuranceCard();

    isSmsLoading: boolean = false;
    smsDisabled: boolean = null;
    smsTimer: number = null;
    smsTimerEnd: Date = null;
    debugAction: string = "success";
    confirmedPhone: string = "";

    protectedAutoCodeData = {
        vinOrBodyOrChassis: false,
        dkNumber: true,
    };
    protectedCarId: string = "";
    paylinkUsed: boolean = false;
    otpMode: boolean = false; // is set from App.vue
}

export default {
    namespaced: true as true,
    state: new InsuranceModule(),

    mutations: {
        toggleLoading(state: InsuranceModule, value: boolean) {
            if (value !== undefined) state.isLoading = value !== undefined ? value : !state.isLoading;
        },
        setCard(state: InsuranceModule, data: InsuranceCard) {
            if (data) {
                // old recent cards fix
                data.drivers.forEach((drv) => {
                    if (drv.oldSurname == undefined) {
                        drv.oldSurname = "";
                        drv.oldDriverLicense = new Document({ docTypeId: 1 });
                    }
                });
                state.card = DefaultMapping(data) as InsuranceCard;
                state.card.guid = uuidv4();
            } else state.card = new InsuranceCard();

            state.card = plainToClass(InsuranceCard, state.card);
        },
        setStep(state: InsuranceModule, newStep: number) {
            state.step = newStep;
        },
        setAutoCodeResponse(state: InsuranceModule, data: AutocodeResponse) {
            console.dir(data);
            if (data.markId === "0") data.markId = null; // wait for a backend fix
            if (data.modelId === "0") data.modelId = null; // wait for a backend fix
            if (data.vin || data.bodyNumber || data.chassisNumber) {
                state.protectedAutoCodeData.vinOrBodyOrChassis = true;
                state.protectedCarId = (data.vin || data.bodyNumber || data.chassisNumber).replace(" ", "");
            }

            const car = state.card.car;
            if (data.vin) car.vin = data.vin;
            if (data.bodyNumber) car.bodyNumber = data.bodyNumber.replace(/[^a-zA-Z0-9]+/, "");
            if (data.chassisNumber) car.chassisNumber = data.chassisNumber.replace(/[^a-zA-Z0-9]+/, "");
            if (data.year) car.yearIssue = data.year;
            if (data.power) car.power = Math.round(data.power);
            if (data.markId) car.markId = data.markId;
            if (data.modelId) car.modelId = data.modelId;
            if (car.modelId) {
                car.modificationId =
                    (this as any).state.dicti.modifications // Не рекомендуется делать (this as any), лучше прокинуть сверху
                        .find((x) => x.power == car.power && x.manufactureStart <= car.yearIssue && x.manufactureEnd >= car.yearIssue)?.id ?? null;
            }
            if (!!data.stsDate && !!data.stsNumber && !!data.stsSeries) {
                car.documentCar.docTypeId = CarDocumentType.STS;
                car.documentCar.docDate = data.stsDate.parseDate();
                car.documentCar.seria = data.stsSeries;
                car.documentCar.number = data.stsNumber;
            } else if (data.ptsDate && data.ptsNumber && data.ptsSeries) {
                car.documentCar.docTypeId = CarDocumentType.PTS;
                car.documentCar.docDate = data.ptsDate.parseDate();

                if (state.card) {
                    const savedCard = RecentCardsHelper.getCard(RecentCardsHelper.createCardName(this as any, state.card));

                    car.documentCar.seria = savedCard?.card?.car?.documentCar?.seria ?? data.ptsSeries;
                    car.documentCar.number = savedCard?.card?.car?.documentCar?.number ?? data.ptsNumber;
                } else {
                    car.documentCar.seria = data.ptsSeries;
                    car.documentCar.number = data.ptsNumber;
                }
            }

            car.documentsCar = [];

            if (!!data.stsDate && !!data.stsNumber && !!data.stsSeries) {
                const carDoc = new Document();
                carDoc.docTypeId = CarDocumentType.STS;
                carDoc.docDate = data.stsDate.parseDate();
                carDoc.seria = data.stsSeries;
                carDoc.number = data.stsNumber;
                car.documentsCar.push(carDoc);
            }
            if (!!data.ptsDate && !!data.ptsNumber && !!data.ptsSeries) {
                const carDoc = new Document();
                carDoc.docTypeId = CarDocumentType.PTS;
                carDoc.docDate = data.ptsDate.parseDate();
                carDoc.seria = data.ptsSeries;
                carDoc.number = data.ptsNumber;
                car.documentsCar.push(carDoc);
            }
        },
        setWithoutGosSign(state: InsuranceModule, isWithoutGosSign: boolean) {
            const docExist = (docType) => {
                return !!state.card.car.documentsCar?.find?.((x) => x.docTypeId == docType);
            };

            let docType: number;
            if (isWithoutGosSign) {
                // Задаём "Без номера", очищаем введённый СТС
                state.card.car.licensePlate = null;
                docType = CarDocumentType.PTS;
                if (!docExist(docType) && state.card.car.documentCar.docTypeId != docType) {
                    state.card.car.documentCar.seria = "";
                    state.card.car.documentCar.number = "";
                }
                state.card.car.documentCar.docTypeId = docType;
            } else {
                // Убираем "Без номера", оставляем введённый ПТС, если он введён
                const noInput = !state.card.car.documentCar.seria.length && !state.card.car.documentCar.number.length;
                if (noInput || docExist(CarDocumentType.STS)) state.card.car.documentCar.docTypeId = CarDocumentType.STS;
            }
        },
        setAutocodeDocumentCar(state: InsuranceModule, car: Car) {
            if (car.documentCar.docTypeId == CarDocumentType.EPTS || car.documentsCar?.length == 0) return;

            const docType = car.documentCar.docTypeId ?? CarDocumentType.STS;
            const doc = car.documentsCar?.find?.((x) => x.docTypeId == docType);
            if (!doc) return;
            car.documentCar.docTypeId = doc.docTypeId;
            car.documentCar.docDate = doc.docDate;

            const savedCard = RecentCardsHelper.getCard(RecentCardsHelper.createCardName(this as any, state.card));
            car.documentCar.seria = savedCard?.card.car.documentCar.seria || doc.seria;
            car.documentCar.number = savedCard?.card.car.documentCar.number || doc.number;
        },
        setSmsTimer(state: InsuranceModule, time: number) {
            state.smsDisabled = true;
            state.smsTimer = time;
            setTimeout(() => {
                state.smsDisabled = false;
            }, state.smsTimer);
            const timer = setInterval(() => {
                if (state.smsTimer == 0) {
                    clearInterval(timer);
                    state.smsTimer = null;
                } else state.smsTimer -= 1000;
            }, 1000);
        },
        initSmsTimers(state: InsuranceModule) {
            let smsTimerEnd = null;
            try {
                window?.localStorage;
                smsTimerEnd = localStorage.smsTimerEnd;
            } catch (e) {
                smsTimerEnd = state.smsTimerEnd;
            }
            if (state.smsTimer <= 0 && new Date().getTime() < smsTimerEnd) {
                const time = Math.floor((smsTimerEnd - new Date().getTime()) / 1000) * 1000;
                // @ts-ignore
                this.commit("insurance/setSmsTimer", time); // 🤡
            }
        },
        setPaylinkUsed(state: InsuranceModule) {
            state.paylinkUsed = true;
        },
        async setOtpBankData(state: InsuranceModule, otpBankData: TokenData) {
            const otpData = otpBankData?.personalData;
            const insurer = state.card.insurer;
            if (!otpData) return;
            insurer.phone = otpData.phone?.trim?.();
            insurer.email = otpData.email?.trim?.();
            const capitalize = (val) => {
                // капитализирует каждое слово на случай сдвоенных фамилий и отчеств
                const parts = val
                    .trim()
                    .toLowerCase()
                    .split("-");
                return parts.map((part) => `${part[0].toUpperCase()}${part.slice(1)}`).join("-");
            };
            insurer.name = capitalize(otpData.firstName);
            insurer.surname = capitalize(otpData.lastName);
            insurer.patronymic = capitalize(otpData.middleName);
            insurer.birthDate = new Date(otpData.birthDate);
            insurer.passport.seria = otpData.passportNumber.slice?.(0, 4).trim?.();
            insurer.passport.number = otpData.passportNumber.slice?.(5, 11).trim?.();
            insurer.passport.divisionCode = otpData.passportIssuerCode.trim?.();
            let passportDate = new Date(otpData.passportIssueDate);
            if (`${passportDate}` === "Invalid Date") passportDate = null;
            insurer.passport.docDate = passportDate;
            insurer.passport.placeOfIssue = otpData.passportIssuer.trim?.();
            let gender;
            if (otpData.gender == OTPBankGender.Male) gender = Sex.Male;
            else gender = Sex.Female;
            insurer.gender = gender;
            try {
                console.log("trying to get address");
                const addrResp = await GeoService.makeQuery(otpData.registrationAddress);
                insurer.address = addrResp?.[0]?.data;
                console.log("otp address set", insurer.address);
            } catch (e) {
                console.error("address lookup failed", e);
            }
            // copypaste just in case
            const personJSON = JSON.stringify(insurer);
            state.card.drivers[0].type = DriverType.Insurer;
            state.card.drivers[0].person = DefaultMapping(JSON.parse(personJSON));
            state.card.owner = DefaultMapping(JSON.parse(personJSON));
        },
    },
    actions: {
        async init({ state, commit, dispatch }, cardId: string = ""): Promise<InsuranceCard> {
            state.isLoading = true;

            if (cardId) {
                const response = await CardService.getCard({ cardId: cardId });
                if (response.success) {
                    await dispatch("dicti/getMapModels", response.data.car.markId, { root: true });
                    await dispatch("dicti/getModifications", response.data.car.modelId, { root: true });
                    response.data.id = cardId;
                    commit("setCard", response.data);
                }
            }

            //load map mark
            await dispatch("dicti/getMapMarks", null, { root: true });
            //load providers
            await dispatch("dicti/getInsuranceProviders", null, { root: true });

            state.isLoading = false;
            return state.card;
        },
        async saveAndFullIssue({ state }, card: InsuranceCard): Promise<ApiResponse<string>> {
            // TODO: check if signature corrent on RM-***-stable
            state.isSaving = true;
            const cardId = card.guid;
            console.log(cardId, card.id, card.guid);
            //card.beginDate = card.beginDate.toPicker() as unknown as Date;
            if (card.insurerIsOwner) card.owner = card.insurer;

            card.drivers.forEach((drv) => {
                if (drv.type == DriverType.Insurer &&
                    (drv.person.surname != card.insurer.surname ||
                    drv.person.name != card.insurer.name ||
                    drv.person.patronymic != card.insurer.patronymic ||
                    drv.person.birthDate != card.insurer.birthDate )) {
                        drv.type = DriverType.Other;
                    }
            });

            const res = await CardService.saveAndFullIssue({
                body: card,
                traceId: cardId,
                testAction: state.debugAction, //"success"// debug only
                // selectedIc
            });
            if (res.success) {
                state.card.id = res.data;
                card.id = res.data;
            }
            state.isSaving = false;
            // Finish.vue on created() starts polling;
            return res;
        },
        async saveIssue({ state, commit }, payload: { cardId: any; icName: string }): Promise<ApiResponse> {
            state.paylinkUsed = true;
            commit("iccalc/disposeChecker", null, { root: true });
            return await InsuranceService.saveIssue(payload);
        },
        async sendPhoneCode(
            { state, commit }: { state: InsuranceModule; commit: any },
            phone: string
        ): Promise<{ success: boolean; error?: string; data?: boolean }> {
            let smsTimerEnd = null;
            try {
                window?.localStorage;
                smsTimerEnd = localStorage.smsTimerEnd;
            } catch (e) {
                smsTimerEnd = state.smsTimerEnd;
            }
            if (new Date().getTime() < smsTimerEnd) {
                const time = Math.floor((smsTimerEnd - new Date().getTime()) / 1000) * 1000;
                commit("setSmsTimer", time);
                return { success: false, error: "Повторите попытку позже" };
            }

            if (state.isSmsLoading) return { success: false, error: "Запрос выполняется" };
            state.isSmsLoading = true;
            const resp = await UserService.sendCode({ phone: phone });

            smsTimerEnd = new Date().addMinutes(1).getTime();
            commit("setSmsTimer", 60000);
            state.isSmsLoading = false;
            return resp;
        },
        async checkPhoneCode({}: { state: InsuranceModule }, model: { phone: string; code: string }): Promise<ApiResponse> {
            const res = await UserService.checkCode({ phone: model.phone, code: model.code });
            return res;
        },
        async checkPhoneStatus({}: { state: InsuranceModule }, phone: string): Promise<ApiResponse> {
            const res = await UserService.checkStatus({ phone });
            return res;
        },
    },
    getters: {},
};
